import {
  forwardRef,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
  type MouseEvent,
} from 'react';
import { createPortal } from 'react-dom';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

export interface ModalProps {
  /** 모달 열림 상태 */
  isOpen: boolean;
  /** 모달 닫기 핸들러 */
  onClose: () => void;
  /** 모달 제목 */
  title?: string;
  /** 모달 내용 */
  children: ReactNode;
  /** 모달 크기 */
  size?: ModalSize;
  /** 오버레이 클릭으로 닫기 허용 */
  closeOnOverlayClick?: boolean;
  /** Escape 키로 닫기 허용 */
  closeOnEscape?: boolean;
  /** 닫기 버튼 표시 */
  showCloseButton?: boolean;
  /** 푸터 영역 */
  footer?: ReactNode;
  /** 추가 클래스 */
  className?: string;
  /** 오버레이 클래스 */
  overlayClassName?: string;
}

const sizeClasses: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  full: 'max-w-[90vw] max-h-[90vh]',
};

export const Modal = forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      isOpen,
      onClose,
      title,
      children,
      size = 'md',
      closeOnOverlayClick = true,
      closeOnEscape = true,
      showCloseButton = true,
      footer,
      className = '',
      overlayClassName = '',
    },
    ref
  ) => {
    const modalRef = useRef<HTMLDivElement>(null);
    const previousActiveElement = useRef<HTMLElement | null>(null);

    // Escape 키 핸들러
    useEffect(() => {
      if (!isOpen || !closeOnEscape) return;

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, closeOnEscape, onClose]);

    // 포커스 트랩 및 스크롤 락
    useEffect(() => {
      if (!isOpen) return;

      // 현재 포커스된 요소 저장
      previousActiveElement.current = document.activeElement as HTMLElement;

      // 모달에 포커스
      const focusableElement = modalRef.current?.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      focusableElement?.focus();

      // 스크롤 락
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = originalOverflow;
        // 이전 포커스 복원
        previousActiveElement.current?.focus();
      };
    }, [isOpen]);

    // 오버레이 클릭 핸들러
    const handleOverlayClick = useCallback(
      (e: MouseEvent) => {
        if (closeOnOverlayClick && e.target === e.currentTarget) {
          onClose();
        }
      },
      [closeOnOverlayClick, onClose]
    );

    if (!isOpen) return null;

    const modalContent = (
      <div
        className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${overlayClassName}`}
        onClick={handleOverlayClick}
        role="presentation"
      >
        {/* 오버레이 백드롭 */}
        <div
          className="absolute inset-0 bg-text/50 backdrop-blur-sm animate-fade-in"
          aria-hidden="true"
        />

        {/* 모달 컨텐츠 */}
        <div
          ref={(node) => {
            modalRef.current = node;
            if (typeof ref === 'function') {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? 'modal-title' : undefined}
          className={`relative bg-white rounded-2xl shadow-2xl w-full ${sizeClasses[size]} animate-modal-enter ${className}`}
        >
          {/* 헤더 */}
          {(title || showCloseButton) && (
            <div className="flex items-center justify-between px-6 py-4 border-b border-text/10">
              {title && (
                <h2
                  id="modal-title"
                  className="font-en text-xl font-semibold text-text"
                >
                  {title}
                </h2>
              )}
              {showCloseButton && (
                <button
                  onClick={onClose}
                  className="ml-auto p-2 text-text/60 hover:text-text rounded-lg hover:bg-text/5 transition-colors"
                  aria-label="닫기"
                >
                  <CloseIcon />
                </button>
              )}
            </div>
          )}

          {/* 본문 */}
          <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">{children}</div>

          {/* 푸터 */}
          {footer && (
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-text/10">
              {footer}
            </div>
          )}
        </div>
      </div>
    );

    // 포털로 body에 렌더링
    return createPortal(modalContent, document.body);
  }
);

Modal.displayName = 'Modal';

function CloseIcon() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}

// 확인 다이얼로그 컴포넌트
export interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: 'primary' | 'danger';
  isLoading?: boolean;
}

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title = '확인',
  message,
  confirmLabel = '확인',
  cancelLabel = '취소',
  confirmVariant = 'primary',
  isLoading = false,
}: ConfirmDialogProps) {
  const handleConfirm = () => {
    onConfirm();
  };

  const confirmButtonClass =
    confirmVariant === 'danger'
      ? 'bg-red-500 hover:bg-red-600 text-white'
      : 'bg-accent hover:bg-accent/90 text-white';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      closeOnOverlayClick={!isLoading}
      closeOnEscape={!isLoading}
      showCloseButton={!isLoading}
      footer={
        <>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 font-kr text-sm text-text/70 hover:text-text disabled:opacity-50 transition-colors"
          >
            {cancelLabel}
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className={`px-4 py-2 font-kr text-sm rounded-lg disabled:opacity-50 transition-colors ${confirmButtonClass}`}
          >
            {isLoading ? '처리 중...' : confirmLabel}
          </button>
        </>
      }
    >
      <p className="font-kr text-text/80">{message}</p>
    </Modal>
  );
}

// 알림 다이얼로그 컴포넌트
export interface AlertDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message: string;
  buttonLabel?: string;
  variant?: 'info' | 'success' | 'warning' | 'error';
}

export function AlertDialog({
  isOpen,
  onClose,
  title,
  message,
  buttonLabel = '확인',
  variant = 'info',
}: AlertDialogProps) {
  const iconMap = {
    info: <InfoIcon className="text-blue-500" />,
    success: <SuccessIcon className="text-green-500" />,
    warning: <WarningIcon className="text-yellow-500" />,
    error: <ErrorAlertIcon className="text-red-500" />,
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      showCloseButton={false}
      footer={
        <button
          onClick={onClose}
          className="px-4 py-2 font-kr text-sm bg-text text-white rounded-lg hover:bg-text/90 transition-colors"
        >
          {buttonLabel}
        </button>
      }
    >
      <div className="flex flex-col items-center text-center">
        <div className="w-12 h-12 mb-4">{iconMap[variant]}</div>
        {title && (
          <h3 className="font-en text-lg font-semibold text-text mb-2">
            {title}
          </h3>
        )}
        <p className="font-kr text-text/80">{message}</p>
      </div>
    </Modal>
  );
}

// 아이콘 컴포넌트들
function InfoIcon({ className = '' }: { className?: string }) {
  return (
    <svg
      className={`w-full h-full ${className}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"
      />
    </svg>
  );
}

function SuccessIcon({ className = '' }: { className?: string }) {
  return (
    <svg
      className={`w-full h-full ${className}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
      />
    </svg>
  );
}

function WarningIcon({ className = '' }: { className?: string }) {
  return (
    <svg
      className={`w-full h-full ${className}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
      />
    </svg>
  );
}

function ErrorAlertIcon({ className = '' }: { className?: string }) {
  return (
    <svg
      className={`w-full h-full ${className}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
      />
    </svg>
  );
}